import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Filter,
  Shield,
  User as UserIcon,
  Clock,
  Globe as GlobeIcon,
  Monitor,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]";

const ROLE_TONES = {
  MASTER_ADMIN: "bg-amber-50 text-amber-700 border-amber-200",
  MASTER_AGENT: "bg-blue-50 text-blue-700 border-blue-200",
  AGENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CASH_VERIFIER: "bg-violet-50 text-violet-700 border-violet-200",
};

const STATUS_TONES = {
  SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
  FAILED: "bg-rose-50 text-rose-700 border-rose-200",
};

const HIGHLIGHT_ACTIONS = new Set([
  "LOGIN_FAILED",
  "CASH_WITHDRAW",
  "WITHDRAW",
  "MANUAL_WALLET_FUND",
  "EXCHANGE_RATE_UPSERT",
  "FEE_TIERS_REPLACE",
  "MASTER_AGENT_CREATE",
  "CASH_VERIFIER_CREATE",
]);

const PAGE_SIZE = 25;

function truncate(s, n = 90) {
  if (!s) return "";
  const str = typeof s === "string" ? s : JSON.stringify(s);
  return str.length > n ? str.slice(0, n) + "…" : str;
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { dateStyle: "short", timeStyle: "medium" });
}

// Human labels for the raw action codes. Anything not in this map is shown
// verbatim (uppercase with underscores) so newly-added actions still work.
const ACTION_LABELS = {
  LOGIN: "Login",
  LOGOUT: "Logout",
  LOGIN_FAILED: "Failed login",
  LOGIN_NEW_DEVICE: "Login from new device",
  VERIFY_2FA_WRONG: "2FA wrong code",
  VERIFY_2FA_EXPIRED: "2FA expired code",
  VERIFY_2FA_MAX_ATTEMPTS: "2FA locked",
  LOGIN_2FA_RESEND: "2FA code resent",
  TRUSTED_DEVICE_ADD: "Trusted device added",
  TRUSTED_DEVICE_REMOVE: "Trusted device removed",
  TRUSTED_DEVICE_FORCE_LOGOUT: "Force logout",
  TRUSTED_DEVICE_FORCE_LOGOUT_SELF: "Force logout (self)",
  PENDING_CODE_VIEW: "Pending code viewed",
  SECURITY_CONFIG_UPDATE: "Security config updated",
  PASSWORD_RESET_REQUEST: "Password reset requested",
  PASSWORD_RESET_COMPLETE: "Password reset completed",
  ADMIN_EMERGENCY_RECOVERY: "Admin emergency recovery",
  ADMIN_EMERGENCY_RECOVERY_FAILED: "Admin emergency recovery — failed",
  AGENT_CREATE: "Agent created",
  AGENT_UPDATE: "Agent updated",
  AGENT_PASSWORD_RESET: "Agent password reset",
  MASTER_AGENT_CREATE: "Master Agent created",
  MASTER_AGENT_UPDATE: "Master Agent updated",
  MASTER_AGENT_PASSWORD_RESET: "Master Agent password reset",
  CASH_VERIFIER_CREATE: "Cash Verifier created",
  CASH_VERIFIER_UPDATE: "Cash Verifier updated",
  TRANSFER: "Wallet transfer",
  WITHDRAW: "Wallet withdraw",
  CASH_TRANSFER: "Cash transfer",
  CASH_WITHDRAW: "Cash withdraw",
  CASH_FUNDING: "Cash wallet funding",
  SEND_CASH_CREATE: "Send Cash created",
  SEND_CASH_SUCCESS: "Send Cash confirmed",
  SEND_CASH_FAILED: "Send Cash rejected",
  TOPUP_SEND: "Send Topup",
  PROFILE_UPDATE: "Profile update",
  BILLING_UPDATE: "Billing update",
  PASSWORD_CHANGE: "Password change",
  PAYKU_CHECKOUT_CREATED: "Payku checkout created",
  PAYKU_CHECKOUT_CREDIT: "Payku wallet credit",
  PAYKU_WALLET_FUND_SUCCESS: "Payku wallet funded",
  MANUAL_WALLET_FUND: "Manual wallet funding",
  MP_WALLET_FUND_SUCCESS: "MercadoPago wallet funded",
  EXCHANGE_RATE_UPSERT: "Exchange rate updated",
  FEE_TIERS_REPLACE: "Fee tiers replaced",
  CATALOG_SYNC: "Provider catalog sync",
  RECONCILE: "Provider reconciled",
  CURRENCY_SETTINGS_UPDATE: "Currency settings",
  PUBLIC_REGISTER: "Public registration",
  RECEIPT_EMAIL: "Receipt emailed",
  CARD_ADD: "Card added",
  CARD_DELETE: "Card deleted",
  CARD_SET_DEFAULT: "Default card set",
  PAYKU_RECONCILE_ALL: "Payku bulk reconcile",
};

export default function AuditLog() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);   // zero-indexed
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // Filter state
  const [facets, setFacets] = useState({ actions: [], roles: [], statuses: [] });
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [role, setRole] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/audit-logs/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = async (nextPage = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(nextPage * PAGE_SIZE),
      });
      if (q.trim()) params.set("q", q.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (role) params.set("role", role);
      if (action) params.set("action", action);
      if (status) params.set("status", status);
      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("audit.failed_load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => load(0);

  const clearFilters = () => {
    setQ("");
    setDateFrom("");
    setDateTo("");
    setRole("");
    setAction("");
    setStatus("");
    setTimeout(() => load(0), 0);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const title = isAdmin ? t("audit.title") : t("audit.title_activity");
  const subtitle = t("audit.subtitle");

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-slate-500" />
            {isAdmin ? t("audit.admin_scope") : t("audit.activity_scope")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {title}
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-3xl">{subtitle}</p>
        </div>
        <button
          onClick={() => load(page)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
          data-testid="audit-reload"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t("audit.reload")}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("audit.search")}
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="audit-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                className={`${inputCls} pl-9`}
                placeholder={t("audit.search_ph")}
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("audit.from")}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={inputCls}
              data-testid="audit-date-from"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("audit.to")}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={inputCls}
              data-testid="audit-date-to"
            />
          </div>
          {isAdmin && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("audit.role")}
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={inputCls}
                data-testid="audit-role"
              >
                <option value="">{t("audit.all_roles")}</option>
                {facets.roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("audit.action")}
            </label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className={inputCls}
              data-testid="audit-action"
            >
              <option value="">{t("audit.all_actions")}</option>
              {facets.actions.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            {(["", "SUCCESS", "FAILED"]).map((s) => (
              <button
                key={s || "all"}
                onClick={() => { setStatus(s); setTimeout(applyFilters, 0); }}
                className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded border ${
                  status === s
                    ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`audit-status-${s || "all"}`}
              >
                {s === "SUCCESS" ? t("audit.status_success") : s === "FAILED" ? t("audit.status_failed") : t("audit.any_status")}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
              data-testid="audit-clear"
            >
              {t("audit.clear")}
            </button>
            <button
              onClick={applyFilters}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6]"
              data-testid="audit-apply"
            >
              <Filter className="w-3.5 h-3.5" /> {t("audit.apply")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr>
                {[
                  { k: "when", label: t("audit.col_when") },
                  { k: "user", label: t("audit.col_user") },
                  { k: "role", label: t("audit.col_role") },
                  { k: "action", label: t("audit.col_action") },
                  { k: "target", label: t("audit.col_target") },
                  { k: "status", label: t("audit.col_status") },
                  ...(isAdmin ? [{ k: "ip", label: t("audit.col_ip") }] : []),
                  { k: "details", label: t("audit.col_details") },
                ].map(({ k, label }) => (
                  <th
                    key={k}
                    className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-5 py-10 text-center text-slate-500 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("audit.loading_trail")}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-5 py-10 text-center text-slate-500 text-sm">
                    {t("audit.no_records")}
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`audit-row-${r.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                    HIGHLIGHT_ACTIONS.has(r.action) ? "bg-amber-50/30" : ""
                  }`}
                  onClick={() => setSelected(r)}
                >
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-mono text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-slate-400" />
                      {formatWhen(r.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-900 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-semibold">{r.actor_username || "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.actor_role ? (
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${ROLE_TONES[r.actor_role] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {r.actor_role.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-900 whitespace-nowrap">
                    <span className="font-semibold">{ACTION_LABELS[r.action] || r.action}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {r.target_username ? (
                      <span className="font-mono text-xs">{r.target_username}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">{r.target_type || "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_TONES[r.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {r.status || "SUCCESS"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                      {r.ip || "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[420px]">
                    {truncate(r.payload, 90)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 text-xs text-slate-500">
          <div>
            {t("audit.records_total", { n: total.toLocaleString(), page: page + 1, total: pageCount })}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => load(Math.max(0, page - 1))}
              disabled={page === 0 || loading}
              className="inline-flex items-center gap-1 h-8 px-2 rounded border border-slate-300 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              data-testid="audit-page-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> {t("audit.prev")}
            </button>
            <button
              onClick={() => load(Math.min(pageCount - 1, page + 1))}
              disabled={page + 1 >= pageCount || loading}
              className="inline-flex items-center gap-1 h-8 px-2 rounded border border-slate-300 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              data-testid="audit-page-next"
            >
              {t("audit.next")} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {selected && <DetailDrawer entry={selected} onClose={() => setSelected(null)} isAdmin={isAdmin} t={t} />}
    </div>
  );
}

function DetailDrawer({ entry, onClose, isAdmin, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-2xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-150" data-testid="audit-detail">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("audit.audit_entry")}</div>
            <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {ACTION_LABELS[entry.action] || entry.action}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto max-h-[calc(90vh-64px)]">
          <DrawerRow label={t("audit.when")} value={formatWhen(entry.created_at)} />
          <DrawerRow label={t("audit.user")} value={entry.actor_username || entry.actor_id || "—"} />
          <DrawerRow label={t("audit.role_label")} value={entry.actor_role || "—"} />
          <DrawerRow label={t("audit.action")} value={entry.action} mono />
          <DrawerRow label={t("audit.target")} value={`${entry.target_type || "—"} · ${entry.target_username || entry.target_id || "—"}`} />
          <DrawerRow label={t("audit.status")} value={entry.status || "SUCCESS"} />
          {entry.reference && <DrawerRow label={t("audit.reference")} value={entry.reference} mono />}
          {isAdmin && (
            <>
              <DrawerRow label={t("audit.ip")} value={entry.ip || "—"} mono icon={<GlobeIcon className="w-3.5 h-3.5 text-slate-400" />} />
              <DrawerRow label={t("audit.device_browser")} value={truncate(entry.user_agent, 200) || "—"} icon={<Monitor className="w-3.5 h-3.5 text-slate-400" />} />
            </>
          )}
          {entry.prev_value !== undefined && (
            <DrawerRow label={t("audit.previous")} value={JSON.stringify(entry.prev_value, null, 2)} mono block />
          )}
          {entry.new_value !== undefined && (
            <DrawerRow label={t("audit.new")} value={JSON.stringify(entry.new_value, null, 2)} mono block />
          )}
          {entry.payload && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{t("audit.payload")}</div>
              <pre className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-[11px] font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(entry.payload, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DrawerRow({ label, value, mono, block, icon }) {
  if (block) {
    return (
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
        <pre className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-[11px] font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap break-words">{value}</pre>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 min-w-[120px] pt-0.5">{label}</div>
      <div className={`text-sm text-slate-800 break-words flex items-center gap-1.5 ${mono ? "font-mono text-xs" : ""}`}>
        {icon}{value}
      </div>
    </div>
  );
}
